import type {
  PartnerAuditInfo,
  PartnerCarrierRate,
  PartnerCarrierRateCreate,
  PartnerCarrierRateSummary as PartnerCarrierRateSummaryBase,
  PartnerCarrierRateUpdate,
  PartnerRateCalendarEntry,
  PartnerRateCalendarEntrySummary,
  PartnerSearchCarrierRateSummaryByTypeParams,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { normalizeOriginCountry } from '~/utils/originCountry';
import { linkMultipleAssets, type BulkLinkPayloads, type LinkAssetPayload } from '../api-utils';

type SingleAssetLinkType = '';
type ArrayAssetLinkType = 'tags';

type CarrierRatesSummaryParams = PartnerSearchCarrierRateSummaryByTypeParams;

interface CarrierRatesPagination {
  totalCount: number | null;
  nextAnchor: string | null;
  previousAnchor: string | null;
}

export interface PaginatedCarrierRateSummary {
  data: PartnerCarrierRateSummary[];
  pagination: CarrierRatesPagination;
}
// ---------------------------------------------------------------------------

// Old carrier rates don't have a rating type
export const getRateCalendarRatingType = (rateCalendar: PartnerRateCalendarEntry) => {
  return rateCalendar.ratingType || (rateCalendar.uncosted ? 'UNCOSTED' : 'SHIPIUM_COSTING');
};

// The generated PMS types (PartnerCarrierRateSummary / PartnerRateCalendarEntrySummary)
// mark every field optional because the OpenAPI schema doesn't annotate `required`,
// but PMS always populates these. Re-tighten the always-present fields so consumers
// keep their non-null guarantees without sprinkling guards across the codebase.
export type PartnerCarrierRateSummaryEntry = PartnerRateCalendarEntrySummary & {
  startDateTime: string;
  endDateTime: string;
  ratingType: NonNullable<PartnerRateCalendarEntrySummary['ratingType']>;
  negotiatedSurchargesCount: number;
};

export type PartnerCarrierRateSummary = Omit<PartnerCarrierRateSummaryBase, 'rateCalendar'> & {
  active: boolean;
  archived: boolean;
  auditInfo: PartnerAuditInfo;
  rateCalendar: PartnerCarrierRateSummaryEntry[];
};

const SUMMARY_URL = '/partner-management-proxy/api/v1/partners/carrierRates/summary';

export const serializeSummaryParams = (params: CarrierRatesSummaryParams) => {
  const { carrierRateType, carrierRateIds, ...rest } = params;
  return {
    ...rest,
    ...(carrierRateType === 'sell' ? { carrierRateType } : {}),
    ...(carrierRateIds && carrierRateIds.length > 0 ? { carrierRateIds: carrierRateIds.join(',') } : {}),
  };
};

// An explicit empty id list scopes to zero rates, but serialized it becomes "no filter" and PMS returns
// the partner's whole corpus. `enabled` can't be relied on to stop it: refetchQueries also refetches
// cached queries nobody is observing (CUS-17063), so the query function itself has to answer empty.
const isEmptyIdScope = (params: CarrierRatesSummaryParams) =>
  Array.isArray(params.carrierRateIds) && params.carrierRateIds.length === 0;

export const parsePaginationHeaders = (headers: Record<string, unknown>): CarrierRatesPagination => {
  const rawTotal = headers['x-total-count'];
  const parsedTotal = rawTotal != null && rawTotal !== '' ? Number(rawTotal) : NaN;
  return {
    totalCount: Number.isFinite(parsedTotal) ? parsedTotal : null,
    nextAnchor: (headers['x-page-next'] as string) || null,
    previousAnchor: (headers['x-page-previous'] as string) || null,
  };
};

export const carrierRates = {
  searchSummary: {
    queryKey: 'partner-management-service/carrier-rates/summary',
    configurationType: 'cached' as const,
    fn: async (params: CarrierRatesSummaryParams = {}): Promise<PartnerCarrierRateSummary[]> => {
      if (isEmptyIdScope(params)) return [];

      const { data } = await GatewayAxiosClient.get<PartnerCarrierRateSummary[]>(SUMMARY_URL, {
        params: serializeSummaryParams(params),
      });

      return data;
    },
  },
  searchSummaryPaginated: {
    queryKey: 'partner-management-service/carrier-rates/summary/paginated',
    configurationType: 'cached' as const,
    fn: async (params: CarrierRatesSummaryParams = {}): Promise<PaginatedCarrierRateSummary> => {
      if (isEmptyIdScope(params)) {
        return { data: [], pagination: { totalCount: 0, nextAnchor: null, previousAnchor: null } };
      }

      const res = await GatewayAxiosClient.get<PartnerCarrierRateSummary[]>(SUMMARY_URL, {
        params: serializeSummaryParams(params),
      });

      return {
        data: res.data,
        pagination: parsePaginationHeaders(res.headers),
      };
    },
  },
  searchSimulation: {
    queryKey: 'partner-management-service/carrier-rates/simulation',
    configurationType: 'cached' as const,
    fn: async ({ simulationId }: { simulationId: string }) => {
      const { data } = await GatewayAxiosClient.get<PartnerCarrierRate[]>(
        `/partner-management-proxy/api/v1/partners/carrierRates`,
        {
          params: { simulationId },
        },
      );

      return data.map((rate) => {
        return {
          ...rate,
          rateCalendar: rate.rateCalendar.map((entry) => {
            return { ...entry, ratingType: getRateCalendarRatingType(entry) };
          }),
        };
      });
    },
  },
  get: {
    queryKey: 'partner-management-service/carrier-rates/id',
    configurationType: 'no-cache' as const,
    fn: async (carrierRateIdentifier: string): Promise<PartnerCarrierRate> => {
      const { data } = await GatewayAxiosClient.get<PartnerCarrierRate>(
        `/partner-management-proxy/api/v1/partners/carrierRates/${carrierRateIdentifier}`,
      );

      return {
        ...data,
        rateCalendar: data.rateCalendar.map((entry) => {
          return {
            ...entry,
            ratingType: getRateCalendarRatingType(entry),
          };
        }),
      };
    },
  },
  /**
   * `originCountry` makes partner-management-service validate the rate calendar against the
   * origin-resolved service method. Without it the zone ids are checked against the base
   * definition, which rejects a rate sheet built for an origin that overrides its zones.
   */
  create: async (carrierRateCreate: PartnerCarrierRateCreate, originCountry?: string | null) => {
    const origin = normalizeOriginCountry(originCountry);
    const { data } = await GatewayAxiosClient.post<PartnerCarrierRate>(
      '/partner-management-proxy/api/v1/partners/carrierRates',
      carrierRateCreate,
      origin ? { params: { originCountry: origin } } : undefined,
    );

    return {
      data,
      refreshQueryKeys: [
        ['partner-management-service/carrier-rates/summary'],
        ['partner-management-service/carrier-rates/summary/paginated'],
        ['partner-management-service/carrier-rates/all-paged'],
        ['partner-management-service/carrier-rates/simulation'],
      ],
    };
  },
  update: async ({ carrierRateId, payload }: { carrierRateId: string; payload: PartnerCarrierRateUpdate }) => {
    const { data } = await GatewayAxiosClient.put<PartnerCarrierRate>(
      `/partner-management-proxy/api/v1/partners/carrierRates/${carrierRateId}`,
      payload,
    );

    return {
      data,
      refreshQueryKeys: [
        ['partner-management-service/carrier-rates/summary'],
        ['partner-management-service/carrier-rates/summary/paginated'],
        ['partner-management-service/carrier-rates/all-paged'],
        ['partner-management-service/carrier-rates/simulation'],
      ],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/carrier-rates/id', carrierRateId],
          data,
        },
      ],
    };
  },
  link: async ({
    carrierRateId,
    payload,
  }: {
    carrierRateId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    const { data } = await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/carrierRates/` +
        `${carrierRateId}/${payload.assetType}/` +
        `${payload.assetId}`,
    });

    return {
      refreshQueryKeys: [
        ['partner-management-service/carrier-rates/summary'],
        ['partner-management-service/carrier-rates/summary/paginated'],
        ['partner-management-service/carrier-rates/all-paged'],
        ['partner-management-service/carrier-rates/simulation'],
      ],
      updateQueryData: [
        {
          queryKey: ['partner-management-service/carrier-rates/id', carrierRateId],
          data,
        },
      ],
    };
  },
  bulkLink: async ({
    carrierRate,
    payloads,
  }: {
    carrierRate: PartnerCarrierRate;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerCarrierRate>(
      payloads,
      {
        tags: carrierRate.tagIds,
      },
      (payload) => carrierRates.link({ carrierRateId: carrierRate.carrierRateId!, payload }),
    );
  },
};
